import React, { useState, useEffect } from 'react';
import { buyerGroupsAPI } from '../services/api';
import ConfirmDialog from './ConfirmDialog';
import './GroupManagementModal.css';

const GroupManagementModal = ({ onClose, onSuccess }) => {
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [view, setView] = useState('list'); // 'list', 'create', 'edit', 'members'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, groupId: null });

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    color: '#3B82F6'
  });


  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const response = await buyerGroupsAPI.getAll();
      const groupData = response.data?.groups || response.data || [];
      setGroups(Array.isArray(groupData) ? groupData : []);
    } catch (err) {
      console.error('Error fetching groups:', err);
      setError('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!formData.name.trim()) {
      setError('Please enter a group name');
      return;
    }

    try {
      setLoading(true);
      await buyerGroupsAPI.create({ name: formData.name, color: formData.color });
      onSuccess && onSuccess('Group created successfully');
      await fetchGroups();
      setView('list');
      setFormData({ name: '', color: '#3B82F6' });
      setError('');
    } catch (err) {
      console.error('Error creating group:', err);
      setError(err.response?.data?.error || 'Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateGroup = async () => {
    if (!formData.name.trim()) {
      setError('Please enter a group name');
      return;
    }

    try {
      setLoading(true);
      await buyerGroupsAPI.update(selectedGroup.id, { name: formData.name, color: formData.color });
      onSuccess && onSuccess('Group updated successfully');
      await fetchGroups();
      setView('list');
      setSelectedGroup(null);
      setError('');
    } catch (err) {
      console.error('Error updating group:', err);
      setError(err.response?.data?.error || 'Failed to update group');
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteGroup = (groupId) => {
    setDeleteConfirm({ isOpen: true, groupId });
  };

  const handleDeleteGroup = async () => {
    const groupId = deleteConfirm.groupId;
    if (!groupId) return;

    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      const response = await buyerGroupsAPI.delete(groupId);

      // Check if deletion was successful
      if (response.data && response.data.success === false) {
        setError(response.data.error || 'Failed to delete group');
        setLoading(false);
        return;
      }

      onSuccess && onSuccess('Group deleted successfully');
      await fetchGroups();
      if (selectedGroup?.id === groupId) {
        setSelectedGroup(null);
        setView('list');
      }
    } catch (err) {
      console.error('Error deleting group:', err);
      // Extract error message from response
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to delete group';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const startEditGroup = (group) => {
    setSelectedGroup(group);
    setFormData({
      name: group.name,
      color: group.color || '#3B82F6'
    });
    setView('edit');
    setError('');
  };

  const startCreateGroup = () => {
    setFormData({ name: '', color: '#3B82F6' });
    setView('create');
    setError('');
  };

  const cancelForm = () => {
    setView('list');
    setSelectedGroup(null);
    setFormData({ name: '', color: '#3B82F6' });
    setError('');
  };

  return (
    <div className="group-management-modal">
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content large" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Manage Groups</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>

          <div className="modal-body">
            {/* Error Display */}
            {error && (
              <div className="error-message" style={{ marginBottom: '1rem' }}>
                {error}
              </div>
            )}

            {/* List View */}
            {view === 'list' && (
              <div className="groups-list-view">
                <div className="list-header">
                  <h3>All Groups ({groups.length})</h3>
                  <button className="btn btn-primary btn-compact" onClick={startCreateGroup}>
                    + Create Group
                  </button>
                </div>

                {loading && <div className="loading-state">Loading groups...</div>}

                {!loading && groups.length === 0 && (
                  <div className="empty-state">
                    <p>No groups created yet.</p>
                    <button className="btn btn-primary" onClick={startCreateGroup}>
                      Create Your First Group
                    </button>
                  </div>
                )}

                {!loading && groups.length > 0 && (
                  <div className="groups-grid">
                    {groups.map(group => (
                      <div key={group.id} className="group-card">
                        <div className="group-card-header">
                          <div className="group-title-row">
                            <span
                              className="group-color-indicator"
                              style={{ backgroundColor: group.color || 'var(--tag)' }}
                            />
                            <h4>{group.name}</h4>
                          </div>
                          <div className="group-actions">
                            <button
                              className="icon-btn"
                              onClick={() => startEditGroup(group)}
                              title="Edit group"
                            >
                              ✏️
                            </button>
                            <button
                              className="icon-btn delete"
                              onClick={() => confirmDeleteGroup(group.id)}
                              title="Delete group"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>

                        {group.description && (
                          <p className="group-description">{group.description}</p>
                        )}

                        <div className="group-stats">
                          <span className="stat">
                            <strong>{group.buyer_count || 0}</strong> prospect{group.buyer_count !== 1 ? 's' : ''}
                          </span>
                          <span className="stat-date">
                            Created {new Date(group.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Create/Edit Form View */}
            {(view === 'create' || view === 'edit') && (
              <div className="group-form-view">
                <div className="form-header">
                  <h3>{view === 'create' ? 'Create New Group' : 'Edit Group'}</h3>
                  <button className="btn btn-secondary btn-compact" onClick={cancelForm}>
                    ← Back to List
                  </button>
                </div>

                <div className="form-group">
                  <label>Group Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Plumbers Leeds & Wakefield"
                    maxLength={100}
                  />
                </div>

                <div className="form-group">
                  <label>Color</label>
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    style={{ width: '80px', height: '40px', padding: '4px', border: '1px solid var(--border-strong)', borderRadius: '4px', cursor: 'pointer' }}
                  />
                </div>

                {error && <div className="error-message">{error}</div>}

                <div className="form-actions">
                  <button className="btn btn-secondary" onClick={cancelForm}>
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={view === 'create' ? handleCreateGroup : handleUpdateGroup}
                    disabled={loading || !formData.name.trim()}
                  >
                    {loading ? 'Saving...' : view === 'create' ? 'Create Group' : 'Update Group'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, groupId: null })}
        onConfirm={handleDeleteGroup}
        title="Delete Group"
        message="Are you sure you want to delete this group? This will not delete the prospects, only remove them from the group."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default GroupManagementModal;
